/**
 * useCalendar — Price Calendar Composable
 *
 * This composable drives the price calendar UI. Here's how the pieces fit together:
 *
 * INITIALISATION
 *   init() is called once with the date range, weekday labels, and today's ISO date.
 *   It builds `calendarMonths` — a nested structure of years → months → weeks → days —
 *   and mirrors every day object into `dayMap` (keyed by ISO date string) so that any
 *   part of the code can cheaply read or mutate a single day without traversing the tree.
 *
 * PRICE LOADING — two separate paths depending on context:
 *
 *   1. Visibility-driven (IntersectionObserver, used on initial load and after filter changes)
 *      setupVisibilityObserver() watches every `.Calendar__day[data-date]` element.
 *      When a cell scrolls into view, enqueueVisibleDates() adds its ISO date to
 *      `requestQueue` and calls processQueue(), which drains the queue sequentially,
 *      calling fetchDataForDate() (a simple PriceCalendarAPI/searchGrid call) for each date.
 *
 *   2. On-demand / deep search (triggered by a day click when no price is cached)
 *      queueDaySearch() now pushes dates into a true FIFO queue.
 *      processDaySearchQueue() starts queued searches strictly in click order while still
 *      respecting MAX_CONCURRENT_SEARCHES.
 *      runDaySearch() runs a three-step async pipeline:
 *        a. initSearch()   — kicks off the search on the server
 *        b. updateSearch() — polls every 2 s until status === 'complete'
 *        c. getPrice()     — fetches the final price, optionally with active filters
 *      A progress counter increments in the background so the UI can show a bar.
 *
 * STATE UPDATES
 *   All mutations go through patchDay(), which calls Object.assign on the live day
 *   object inside calendarMonths. Because Vue tracks the object reference, the template
 *   reacts automatically. When apiResponse is patched, patchDay also calls
 *   updateCheapestDays() to recompute which day should receive the `isCheapest` flag.
 *
 * USER INTERACTION
 *   handleDayClick() checks whether a price already exists for the date.
 *   - If yes  → opens the flight-detail modal via resultsRender.
 *   - If no   → sets that date as the departure date and kicks off queueDaySearch().
 *
 * FILTER CHANGES
 *   setFilters() clears loadedDates and wipes apiResponse from every non-loading day,
 *   then calls retriggerObserver() so the IntersectionObserver re-fires for all visible
 *   cells, reloading prices with the new filter parameters.
 *
 * SLIDER
 *   scrollCalendars() and updateSliderState() manage the horizontal month-strip scroller,
 *   snapping scroll position to whole calendar widths and keeping the left/right arrow
 *   buttons enabled/disabled correctly.
 */

import { computed, markRaw, ref } from 'vue';
import {
	dateAdd,
	dateAddMonths,
	dateAddWeeks,
	dateFormat,
	dateFormatYMD,
	dateGetDate,
	dateGetDay,
	dateGetYear,
	dateIsAfter,
	dateIsBefore,
	dateIsToday,
	dateIsWeekend,
	dateParseISO
} from '@utils/dateFns.js';
import { getUrlVarBase64, l, lc, sleep, urlParameterGet } from '@utils/toolbox';
import { ConnectApi } from '@js/plugins/connectApi';
import { getHolidayData } from '@js/holidays/datepicker.holidays';
import { resultsRender } from '@js/components/resultsRender';
import { useResultQueryStore } from '../components/Filters/store/resultQueryStore';


// ── Calendar data state ───────────────────────────────────────────────────────

/** Which date-picker field is active: 'depart' (return not yet implemented). */
const focus = ref('depart');

/** The currently selected departure date as an ISO string (yyyy-MM-dd). */
const departDate = ref(js_params?.showresultSearchParams?.[0]?.departDate);

/** Ordered weekday label keys used as column headers in each month grid. */
const weekdays = ref([]);

/**
 * Nested date-range object provided by the server during init.
 * Shape: { [year]: { months: { [month]: { weeks: { [week]: { days: string[] } } } } } }
 */
const dateRange = ref({});

/** Today's date as an ISO string, set during init. */
const todayISO = ref('');

/** The furthest selectable date (today + 364 days), as an ISO string. */
const lastSelectableDay = ref('');

/** Set of ISO date strings that are public holidays in the current locale. */
const holidays = ref(new Set());

/** Active sort key for price data (e.g. 'price', 'duration'). Reads from the URL first. */
const sorting = ref(urlParameterGet('sorting') || js_params.defaultSorting);

// ── Derived ───────────────────────────────────────────────────────────────────

/** ISO week number of today, used to apply the 'current week' CSS class. */
const currWeekNo = computed(() => {
	if (!todayISO.value) {
		return null;
	}
	return dateFormat(new Date(todayISO.value), 'w');
});

/** Four-digit year of today, paired with currWeekNo for week highlighting. */
const currYear = computed(() => {
	if (!todayISO.value) {
		return null;
	}
	return dateFormat(new Date(todayISO.value), 'yyyy');
});

// ── Day lookup table ──────────────────────────────────────────────────────────

/**
 * Flat Map of ISO date string → day object.
 * Every day rendered in calendarMonths has a live reference stored here so
 * that patchDay() can update any cell in O(1) without tree traversal.
 */
const dayMap = new Map();

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Longest price
 */
const maxPriceDigits = computed(() => {
	const prices = [];
	for (const day of dayMap.values()) {
		const max = getPriceForDate(day.dateISO);
		prices.push(max);
	}
	return Math.max(...prices).toString().length;
});

const maxDigitsClass = computed(() => {
	if (maxPriceDigits.value <= 3) {
		return 'Calendar__price--max-short';
	} else if (maxPriceDigits.value === 4 || maxPriceDigits.value === 5) {
		return 'Calendar__price--max-normal';
	}
	return 'Calendar__price--max-long';
});

/**
 * Merges `patch` into the day object for `dateISO` and, when apiResponse is
 * included in the patch, triggers a full cheapest-day recalculation.
 *
 * All state mutations for day objects should go through this function so that
 * side-effects (cheapest highlighting, etc.) are never missed.
 *
 * @param {string} dateISO - ISO date string identifying the day to update.
 * @param {object} patch   - Partial day properties to merge in.
 */
function patchDay (dateISO, patch) {
	const day = dayMap.get(dateISO);
	if (day) {
		Object.assign(day, patch);
		if ('apiResponse' in patch) {
			updateCheapestDays();
		}
	}
}

// ── Queue state ───────────────────────────────────────────────────────────────

/**
 * FIFO queue of ISO date strings waiting to be fetched via the visibility-driven path.
 * Populated by enqueueVisibleDates(), drained by processQueue().
 */
const requestQueue = [];

/**
 * Tracks dates that are either sitting in requestQueue, in the day-search queue,
 * or currently being fetched/searched, preventing the same date from being
 * enqueued twice across both loading paths.
 */
const queuedOrProcessingDates = new Set();

/** Tracks dates whose price has been successfully loaded, preventing re-fetches. */
const loadedDates = new Set();

/** Guards against processQueue() running more than one drain loop at a time. */
let isProcessingQueue = false;

// ── Concurrency limiter ───────────────────────────────────────────────────────

/** Maximum number of on-demand deep searches (queueDaySearch) running simultaneously. */
const MAX_CONCURRENT_SEARCHES = 3;

/** Reactive counter of in-flight deep searches, checked before starting a new one. */
const activeSearchCount = ref(0);

// ── Deep-search queue state ───────────────────────────────────────────────────

/**
 * FIFO queue for on-demand deep searches, preserving click order.
 */
const daySearchQueue = [];

/**
 * Tracks dates specifically waiting in the deep-search queue so they can be
 * rendered as queued and not added twice.
 */
const queuedDaySearchDates = new Set();

/**
 * Guards against processDaySearchQueue() running more than one dispatcher loop at a time.
 */
let isProcessingDaySearchQueue = false;

/**
 * Fetches price data for a single date via the fast PriceCalendarAPI/searchGrid endpoint.
 * Used by the visibility-driven queue; does not poll — returns immediately with results.
 *
 * @param {string} dateISO
 * @returns {Promise<object>} Raw API response.
 */
async function fetchDataForDate (dateISO) {
	const filters = getUrlVarBase64('filter');
	const data = [
		js_params.id,
		[dateISO],
		filters,
		window.showresult.searchParams.sorting
	];

	return await ConnectApi('PriceCalendarAPI', 'searchGrid', data, true);
}

/**
 * Adds the given ISO dates to the visibility-driven request queue, skipping any
 * that are already loaded, already queued, or marked as disabled.
 * Immediately calls processQueue() to begin draining.
 *
 * @param {string[]} dateISOs - Array of ISO date strings to enqueue.
 */
function enqueueVisibleDates (dateISOs = []) {
	const uniqueDates = [...new Set(dateISOs)];

	for (const dateISO of uniqueDates) {
		if (loadedDates.has(dateISO) || queuedOrProcessingDates.has(dateISO)) {
			continue;
		}

		const day = dayMap.get(dateISO);
		if (!day || day.isDisabled) {
			continue;
		}

		queuedOrProcessingDates.add(dateISO);
		requestQueue.push(dateISO);

		patchDay(dateISO, {
			loading: true,
			error: null
		});
	}

	processQueue();
}

/**
 * Drains requestQueue one item at a time (sequential, not concurrent).
 * Uses an `isProcessingQueue` guard so only one drain loop runs at a time —
 * safe to call repeatedly from the IntersectionObserver callback.
 */
async function processQueue () {
	if (isProcessingQueue) {
		return;
	}

	isProcessingQueue = true;

	const requestQueueLength = requestQueue.length;

	while (requestQueue.length > 0) {
		const dateISO = requestQueue.shift();

		try {
			const result = await fetchDataForDate(dateISO);
			if (!result || result.error) {
				const errorMsg = result?.error || 'Unknown error';
				throw new Error(errorMsg);
			}

			patchDay(dateISO, {
				loading: false,
				apiResponse: markRaw(result),
				error: null,
				responseId: Object.keys(result?.data || {})[0] || null
			});

			loadedDates.add(dateISO);
		} catch (error) {
			patchDay(dateISO, {
				loading: false,
				error: error instanceof Error ? error.message : 'Unknown error'
			});
		} finally {
			queuedOrProcessingDates.delete(dateISO);
		}
	}

	if (requestQueueLength > 0 && !hasResolvedFirstObserverPass) {
		hasResolvedFirstObserverPass = true;
		resolveFirstObserverPass();
	}

	isProcessingQueue = false;
}

// ── Calendar data builders ────────────────────────────────────────────────────

/**
 * Returns a translation key for the day label badge (e.g. "Today", "Depart"),
 * or null if no label applies.
 *
 * @param {string} dateISO
 * @returns {string|null}
 */
function getDayLabelKey (dateISO) {
	if (todayISO.value === dateISO) {
		return 'global.simplePhrases.today';
	}
	if (departDate.value === dateISO) {
		return 'global.simplePhrases.depart';
	}
	return null;
}

/**
 * Computes the list of BEM modifier classes for a calendar day cell.
 * Handles disabled (out-of-range), active/depart, and holiday states.
 *
 * @param {string} dateISO
 * @returns {string[]} Array of CSS class names.
 */
function getDayClasses (dateISO) {
	const dateObj = new Date(dateISO);
	const lastDay = new Date(lastSelectableDay.value);
	const todayObj = new Date(todayISO.value);

	const classes = [];

	const isDisabled
		= !dateIsToday(dateObj)
		&& (dateIsBefore(dateObj, todayObj) || dateIsAfter(dateObj, lastDay));

	if (isDisabled) {
		classes.push('Calendar__day--disabled');
	}
	if (departDate.value === dateISO) {
		classes.push('Calendar__day--active', 'Calendar__day--depart');
	}
	if (holidays.value.has(dateISO)) {
		classes.push('Calendar__day--holiday');
	}

	return classes;
}

/**
 * Builds the full `calendarMonths` array from the raw `dateRange` config.
 * Each month contains its weekday headers, an array of week rows, and within
 * each week row an array of fully-populated day objects ready for the template.
 *
 * Also registers every day object in `dayMap` via init() after this returns.
 *
 * @returns {object[]} Array of month descriptor objects.
 */
function buildCalendarData () {
	const range = dateRange.value;

	const months = [];

	for (const year in range) {
		const monthsData = range[year].months;

		for (const month in monthsData) {
			const weeksData = monthsData[month].weeks;
			const monthDate = new Date(year, month - 1, 1);

			const weekRows = [];

			for (const week in weeksData) {
				const daysData = weeksData[week].days;
				const weekNo = weeksData[week].week;

				const days = [];

				for (const day in daysData) {
					const dateISO = daysData[day];
					const dateObj = dateParseISO(dateISO);
					const dayOfWeek = dateGetDay(dateObj);
					const isWeekend = dateIsWeekend(dateObj);

					const cellClasses = ['Calendar__cell'];
					if (isWeekend) {
						cellClasses.push('Calendar__cell--weekend');
					}
					if (dayOfWeek === 0) {
						cellClasses.push('Calendar__cell--last');
					}

					let weekClass = 'Calendar__week';
					if (weekNo === currWeekNo.value && dateGetYear(dateObj).toString() === currYear.value) {
						weekClass += ' Calendar__week--current';
					} else if (weekNo < currWeekNo.value) {
						weekClass += ' Calendar__week--past';
					}

					const dayObj = {
						dateISO,
						day: dateGetDate(dateObj),
						dateFormattedLong: dateFormat(dateObj, 'd MMMM yyyy'),
						weekNo,
						weekClass,
						cellClasses,
						dayClasses: getDayClasses(dateISO),
						labelKey: getDayLabelKey(dateISO),
						isDisabled:
							!dateIsToday(dateObj)
							&& (dateIsBefore(dateObj, new Date(todayISO.value)) || dateIsAfter(dateObj, new Date(lastSelectableDay.value))),
						queued: false,
						loading: false,
						progress: 0,
						apiResponse: null,
						error: null,
						isCheapest: false
					};

					days.push(dayObj);
				}

				weekRows.push({ weekNo, days });
			}

			months.push({
				key: `${year}-${month}`,
				monthLabel: dateFormat(monthDate, 'MMM'),
				year: dateGetYear(monthDate),
				weekdays: weekdays.value,
				weekRows
			});
		}
	}

	return months;
}

/** Reactive array of month objects consumed directly by the calendar template. */
const calendarMonths = ref([]);

// ── Day search logic ──────────────────────────────────────────────────────────

/**
 * Runs the full three-step deep-search pipeline for a single date:
 *   1. initSearch   — tells the server to start searching
 *   2. updateSearch — polls every 2 s until the search is complete
 *   3. getPrice     — retrieves the final price (with any active filters applied)
 *
 * Updates the day's `progress` counter in the background so the UI can render
 * a progress bar. On success, patches apiResponse. On failure, patches error.
 *
 * @param {string} dateISO
 */
async function runDaySearch (dateISO) {
	let updateInterval = null;
	let progressInterval = null;

	progressInterval = setInterval(() => {
		const day = dayMap.get(dateISO);
		if (day && day.progress < 99) {
			patchDay(dateISO, { progress: day.progress + 1 });
		}
	}, 200);

	const initSearch = () => ConnectApi('Search', 'mimicSearch', [js_params.id, [dateISO]]);

	const updateSearch = (reference) => {
		if (!reference?.results?.server) {
			return Promise.reject(reference);
		}
		return new Promise((resolve, reject) => {
			updateInterval = setInterval(async () => {
				try {
					const data = await ConnectApi('Search', 'update', [{ responseId: reference.results.id }], true);
					if (data?.results?.status === 'complete') {
						clearInterval(updateInterval);
						resolve(reference.results.id);
					} else if (data?.error !== undefined) {
						clearInterval(updateInterval);
						const error = new Error(data.error);
						error.error_code = data.error_code;
						reject(error);
					}
				} catch (err) {
					clearInterval(updateInterval);
					reject(err);
				}
			}, 2000);
		});
	};

	const getPrice = (responseId) => {
		const filters = getUrlVarBase64('filter');
		const data = filters ? [responseId, filters] : [responseId];
		return ConnectApi('PriceCalendarAPI', 'getPrice', data, true);
	};

	try {
		const reference = await initSearch();
		const responseId = await updateSearch(reference);
		const response = await getPrice(responseId);

		patchDay(dateISO, { progress: 100 });
		clearInterval(progressInterval);

		patchDay(dateISO, { loading: false, progress: 0, apiResponse: response, error: null, responseId });
		loadedDates.add(dateISO);
	} catch (error) {
		clearInterval(updateInterval);
		clearInterval(progressInterval);
		patchDay(dateISO, { loading: false, progress: 0, error: true });
	}
}

/**
 * Dispatches queued deep searches in strict FIFO order while still respecting
 * MAX_CONCURRENT_SEARCHES. Only one dispatcher loop may run at a time.
 */
async function processDaySearchQueue () {
	if (isProcessingDaySearchQueue) {
		return;
	}

	isProcessingDaySearchQueue = true;

	try {
		while (daySearchQueue.length > 0) {
			if (activeSearchCount.value >= MAX_CONCURRENT_SEARCHES) {
				await sleep(50);
				continue;
			}

			const dateISO = daySearchQueue.shift();
			queuedDaySearchDates.delete(dateISO);

			const day = dayMap.get(dateISO);
			if (!day || day.isDisabled) {
				queuedOrProcessingDates.delete(dateISO);
				continue;
			}

			activeSearchCount.value++;
			patchDay(dateISO, {
				queued: false,
				loading: true,
				progress: 15,
				error: null,
				apiResponse: null
			});

			runDaySearch(dateISO)
				.finally(() => {
					queuedOrProcessingDates.delete(dateISO);
					activeSearchCount.value--;
					processDaySearchQueue();
				});
		}
	} finally {
		isProcessingDaySearchQueue = false;
	}
}

/**
 * Entry point for on-demand deep searches (triggered by a day click with no cached price).
 * Adds the date to a true FIFO queue so searches start in click order.
 *
 * @param {string} dateISO
 */
function queueDaySearch (dateISO) {
	const day = dayMap.get(dateISO);
	if (!day || day.isDisabled) {
		return;
	}

	if (queuedOrProcessingDates.has(dateISO) || queuedDaySearchDates.has(dateISO)) {
		return;
	}

	queuedOrProcessingDates.add(dateISO);
	queuedDaySearchDates.add(dateISO);
	daySearchQueue.push(dateISO);

	patchDay(dateISO, {
		queued: true,
		loading: false,
		progress: 0,
		error: null
	});

	processDaySearchQueue();
}

// ── Day click handler ─────────────────────────────────────────────────────────

/**
 * Handles a click on a calendar day cell.
 *
 * - If a price is already cached for this date, opens the flight-detail modal.
 *   The current resultsRender response is backed up and restored when the modal closes.
 * - If no price exists and the day is available, sets it as the departure date
 *   and triggers an on-demand deep search via queueDaySearch().
 * - Disabled, loading, or queued days are ignored.
 *
 * @param {string} dateISO
 */
function handleDayClick (dateISO) {
	const day = dayMap.get(dateISO);

	if (getPriceForDate(dateISO)) {
		resultsRender.setData(day.apiResponse, window.showresult.searchParams);

		const flight = getFlightForDate(dateISO);
		resultsRender
			.renderModal(day.responseId, flight, 0, {}, {
				onClose: () => {
					resultsRender.setData(useResultQueryStore().state.response, window.showresult.searchParams);
				}
			}, {}, 'pricecalendar')
			.open();

		return;
	}

	if (!day || day.error || day.isDisabled || day.loading || day.queued || queuedOrProcessingDates.has(dateISO)) {
		return;
	}

	queueDaySearch(dateISO);
}

/**
 * Returns the formatted lowest price for a date, or the string 'Sök' (Swedish for
 * "Search") if no price has been loaded yet. 'Sök' also doubles as the sentinel value
 * that handleDayClick() checks to decide whether to open the modal or start a search.
 *
 * @param {string} dateISO
 * @returns {string|null}
 */
function getPriceForDate (dateISO) {
	const day = dayMap.get(dateISO);
	const values = Object.values(day?.apiResponse?.data || {});
	if (!values.length) {
		return null;
	}
	return values[0][sorting.value].lowestprice;
}

function getLabel (dateISO) {
	const price = getPriceForDate(dateISO);
	return price ? lc(price) : l('global.simplePhrases.search');
}

/**
 * Returns the flight data object for a date (the entry used to populate the modal).
 * Returns undefined if no API response is available.
 *
 * @param {string} dateISO
 * @returns {object|undefined}
 */
function getFlightForDate (dateISO) {
	const day = dayMap.get(dateISO);
	const values = Object.values(day?.apiResponse?.data || {});
	if (day && day.apiResponse && values.length > 0) {
		return values[0][sorting.value];
	}
}

// ── Keyboard navigation helpers ───────────────────────────────────────────────

/**
 * Maps an arrow-key press to the ISO date of the adjacent cell:
 *   ArrowLeft/Right → previous/next month
 *   ArrowUp/Down    → previous/next week
 *   Enter/Space     → returns null (caller handles activation)
 *
 * @param {string} currentISO - The currently focused date.
 * @param {string} key        - The key name from the keyboard event.
 * @returns {string|null} Target ISO date, or null for activation keys.
 */
function getAdjacentDate (currentISO, key) {
	const parsed = dateParseISO(currentISO);
	const fmt = d => dateFormat(d, 'yyyy-MM-dd');

	switch (key) {
		case 'ArrowLeft': return fmt(dateAddMonths(parsed, -1));
		case 'ArrowRight': return fmt(dateAddMonths(parsed, 1));
		case 'ArrowUp': return fmt(dateAddWeeks(parsed, -1));
		case 'ArrowDown': return fmt(dateAddWeeks(parsed, 1));
		case 'Enter':
		case 'Space': {
			return null;
		}
		default: return null;
	}
}

// ── Init ──────────────────────────────────────────────────────────────────────

/**
 * Initialises the composable. Must be called before any other method.
 *
 * Responsibilities:
 *   - Sets todayISO, lastSelectableDay, weekdays, and dateRange from the server config.
 *   - Loads holiday data into the `holidays` set.
 *   - Calls buildCalendarData() to populate calendarMonths.
 *   - Populates dayMap with references to every day object.
 *   - Kicks off enqueueSpiralFromDeparture() to pre-load prices for visible cells.
 *
 * @param {object} config
 * @param {string}   config.todayISO   - Today's date as yyyy-MM-dd.
 * @param {string[]} config.weekdays   - Ordered weekday label keys for column headers.
 * @param {object}   config.dateRange  - Nested date-range structure from the server.
 */
function init (config) {
	const today = new Date(config.todayISO);

	todayISO.value = config.todayISO;
	lastSelectableDay.value = dateFormatYMD(dateAdd(today, { days: 364 }));
	weekdays.value = config.weekdays;
	dateRange.value = config.dateRange;

	const holidayData = getHolidayData();
	const set = new Set();
	for (const hol in holidayData) {
		set.add(hol);
	}
	holidays.value = set;
	calendarMonths.value = buildCalendarData();

	dayMap.clear();
	for (const month of calendarMonths.value) {
		for (const weekRow of month.weekRows) {
			for (const day of weekRow.days) {
				dayMap.set(day.dateISO, day);
			}
		}
	}

	enqueueSpiralFromDeparture();
}

// ── Observer ──────────────────────────────────────────────────────────────────

/** Holds the active IntersectionObserver instance so it can be disconnected on reset. */
let observer = null;

/**
 * Attaches an IntersectionObserver to every `.Calendar__day[data-date]` element.
 * When a day cell enters the viewport (threshold 0.1), its date is passed to
 * enqueueVisibleDates() for price loading.
 *
 * Disconnects any previous observer before creating a new one, making this safe
 * to call after the DOM is re-rendered (e.g. after a month scroll).
 */
function setupVisibilityObserver () {
	if (observer) {
		observer.disconnect();
	}

	observer = new IntersectionObserver((entries) => {
		const visibleDates = [];

		for (const entry of entries) {
			if (entry.isIntersecting) {
				const dateISO = entry.target.dataset.date;
				if (dateISO) {
					visibleDates.push(dateISO);
				}
			}
		}

		if (visibleDates.length) {
			enqueueVisibleDates(visibleDates);
		}
	}, {
		root: null,
		threshold: 0.1
	});

	document.querySelectorAll('.Calendar__day[data-date]').forEach((el) => {
		observer.observe(el);
	});
}

// ── Spiral queue from departure date ──────────────────────────────────────────

/**
 * Pre-loads prices for all currently visible calendar cells, prioritising those
 * closest in time to the departure date (or today if no departure is selected).
 *
 * This creates a "spiral outward" loading pattern: the most relevant dates appear
 * first, making the calendar feel responsive on initial render.
 */
function enqueueSpiralFromDeparture () {
	const pivot = departDate.value || todayISO.value;
	if (!pivot) {
		return;
	}

	const pivotTime = dateParseISO(pivot).getTime();

	const visibleDates = [...document.querySelectorAll('.Calendar__day[data-date]')]
		.reduce((acc, el) => {
			const iso = el.dataset.date;
			const day = dayMap.get(iso);
			const rect = el.getBoundingClientRect();
			const isVisible = rect.top < window.innerHeight && rect.bottom > 0;

			if (day && !day.isDisabled && isVisible) {
				acc.push({ iso, dist: Math.abs(dateParseISO(iso).getTime() - pivotTime) });
			}
			return acc;
		}, [])
		.sort((a, b) => a.dist - b.dist)
		.map(({ iso }) => iso);

	enqueueVisibleDates(visibleDates);
}

/**
 * Resets all loaded price data and re-triggers the IntersectionObserver so that
 * prices are re-fetched using the currently active filters.
 *
 * Days that are mid-flight (loading or queued) are intentionally left untouched
 * to avoid race conditions; they will complete with stale filter params but those
 * results will be overwritten once the observer fires again.
 */
function setFilters () {
	loadedDates.clear();

	for (const [dateISO, day] of dayMap.entries()) {
		if (day.queued || day.loading) {
			continue;
		}
		if (!day.isDisabled && !day.queued && !day.loading) {
			patchDay(dateISO, {
				loading: false,
				queued: false,
				progress: 0,
				apiResponse: null,
				error: null,
				responseId: null
			});
		}
	}

	retriggerObserver();
}

/**
 * Forces the IntersectionObserver to re-evaluate all calendar day elements by
 * briefly unobserving and re-observing each one. Used after a filter change to
 * reload prices for currently visible cells without waiting for the user to scroll.
 */
function retriggerObserver () {
	if (!observer) {
		return;
	}

	document.querySelectorAll('.Calendar__day[data-date]').forEach((el) => {
		observer.unobserve(el);
		observer.observe(el);
	});
}

/**
 * Scans all days with loaded prices and sets the `isCheapest` flag on whichever
 * day has the single lowest price. All other days have `isCheapest` set to false.
 *
 * Called automatically by patchDay() whenever an apiResponse is written, so the
 * "cheapest" highlight stays accurate as prices load progressively.
 */
function updateCheapestDays () {
	let lowestPrice = Infinity;

	for (const [, day] of dayMap.entries()) {
		if (day.isDisabled || !day.apiResponse) {
			continue;
		}
		const values = Object.values(day.apiResponse?.data || {});
		if (values.length === 0) {
			continue;
		}

		const price = values[0][sorting.value]?.lowestprice;
		if (typeof price === 'number' && price < lowestPrice) {
			lowestPrice = price;
		}
	}

	for (const [dateISO, day] of dayMap.entries()) {
		if (day.isDisabled || !day.apiResponse) {
			continue;
		}
		const values = Object.values(day.apiResponse?.data || {});
		if (values.length === 0) {
			continue;
		}

		const price = values[0][sorting.value]?.lowestprice;

		patchDay(dateISO, { isCheapest: typeof price === 'number' && price === lowestPrice });
	}
}

// Find the first date with isCheapest === true and return its dateISO
const firstCheapestDate = computed(() => {
	const firstCheapest = [...dayMap.values()].find((day) => {
		if (day.isDisabled || !day.apiResponse) {
			return false;
		}
		return day.isCheapest;
	});
	return firstCheapest ? firstCheapest.dateISO : null;
});

// ── Calendar slider state ─────────────────────────────────────────────────────

/** True when the horizontal scroller is at the leftmost position (no previous months). */
const leftDisabled = ref(true);

/** True when the horizontal scroller is at the rightmost position (no more months). */
const rightDisabled = ref(false);

/**
 * Syncs the left/right arrow disabled state with the current scroll position of the
 * calendar strip. Should be called from a scroll event listener on the inner container.
 *
 * @param {HTMLElement} inner - The scrollable inner container element.
 */
function updateSliderState (inner) {
	const scrolled = inner.scrollLeft;
	const maxScroll = inner.scrollWidth - inner.offsetWidth;
	leftDisabled.value = scrolled <= 0;
	rightDisabled.value = scrolled >= maxScroll;
}

/**
 * Scrolls the calendar strip left or right by exactly one calendar width,
 * snapping to the nearest whole-calendar boundary for clean month alignment.
 * Also eagerly updates the arrow disabled states before the scroll animation completes.
 *
 * @param {'left'|'right'} direction - Scroll direction.
 * @param {HTMLElement}    innerEl   - The scrollable inner container element.
 */
function scrollCalendars (direction, innerEl) {
	if (!innerEl) {
		return;
	}

	const calendarEl = innerEl.querySelector('.Calendar');
	if (!calendarEl) {
		return;
	}

	const width = calendarEl.offsetWidth;
	const scrolled = innerEl.scrollLeft;
	const maxScroll = innerEl.scrollWidth - innerEl.offsetWidth;

	const rawLeft = direction === 'right' ? scrolled + width : scrolled - width;
	const rounded = Math.round(rawLeft / width) * width;

	leftDisabled.value = rounded <= 0;
	rightDisabled.value = rounded >= maxScroll;

	innerEl.scrollTo({ left: rounded, behavior: 'smooth' });
}

/**
 * Development helper — sets the first five non-disabled days to each possible
 * UI state (default, queued, loading, error, priced) so all states can be
 * inspected visually without waiting for real API responses.
 *
 * @internal Not intended for production use.
 */
function debugState () {
	console.log('????');

	let count = 0;
	for (const [dateISO, day] of dayMap.entries()) {
		if (day.isDisabled) {
			continue;
		}
		if (count === 0) {
			patchDay(dateISO, {
				loading: false,
				queued: false,
				error: null,
				apiResponse: null,
				responseId: null
			});
		} else if (count === 1) {
			console.log('Setting queued state for', dateISO);
			patchDay(dateISO, { queued: true });
		} else if (count === 2) {
			console.log('Setting loading state for', dateISO);
			patchDay(dateISO, { loading: true, progress: 50 });
		} else if (count === 3) {
			console.log('Setting error state for', dateISO);
			patchDay(dateISO, {
				loading: false,
				queued: false,
				error: true,
				apiResponse: null,
				responseId: null
			});
		} else if (count === 4) {
			console.log('Setting price for', dateISO, { apiResponse: { data: { 123465: { [sorting.value]: [{ lowestprice: 123 }] } } } });
			patchDay(dateISO, { apiResponse: { data: { 123465: { [sorting.value]: [{ lowestprice: 123 }] } } } });
		}
		count++;
		if (count > 4) {
			break;
		}
	}
}

// ── Nearby Dates ──────────────────────────────────────────────────────────────

let resolveFirstObserverPass;
const firstObserverPass = new Promise((resolve) => {
	resolveFirstObserverPass = resolve;
});
let hasResolvedFirstObserverPass = false;

async function queueNearbyDates () {
	await firstObserverPass;

	for (let offset = -3; offset <= 3; offset++) {
		if (offset === 0) {
			continue;
		}

		const dateISO = dateFormatYMD(dateAdd(departDate.value, { days: offset }));

		// Check if day has already been loaded or is disabled before queuing
		const day = dayMap.get(dateISO);
		if (day && !day.isDisabled && !getPriceForDate(dateISO) && !queuedOrProcessingDates.has(dateISO)) {
			queueDaySearch(dateISO);
			await sleep(1);
		}
	}
}

// ── Export ────────────────────────────────────────────────────────────────────

/**
 * Exposes the calendar composable's public API.
 * Import and destructure in any component that renders or interacts with the calendar.
 */
export function useCalendar () {
	return {
		calendarMonths,
		weekdays,
		focus,
		departDate,
		init,
		handleDayClick,
		getAdjacentDate,
		setupVisibilityObserver,
		sorting,
		getFlightForDate,
		getPriceForDate,
		maxDigitsClass,
		getLabel,
		setFilters,
		leftDisabled,
		rightDisabled,
		scrollCalendars,
		updateCheapestDays,
		updateSliderState,
		queueNearbyDates,
		debugState,
		firstCheapestDate
	};
}
